import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/api";
import { Creator } from "@/types";

const inputCls = "w-full border border-stone-300 px-3 py-2 text-sm focus:border-stone-900 focus:outline-none";
const btnCls = "border border-stone-300 px-2 py-1 text-xs font-semibold hover:bg-stone-50";

export default function AdminVideos() {
  const [creators, setCreators] = useState<Creator[]>([]);
  const [creatorId, setCreatorId] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ title: "", youtube_url: "" });

  const load = () =>
    api.get("/admin/creators").then((r) => {
      setCreators(r.data);
      if (!creatorId && r.data.length) setCreatorId(r.data[0].id);
    }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const creator = creators.find((c) => c.id === creatorId);
  const videos = creator?.videos || [];

  const save = async () => {
    if (!form.title.trim() || !form.youtube_url.trim()) { toast.error("Title and YouTube URL are required"); return; }
    try {
      if (editingId) await api.put(`/admin/videos/${editingId}`, form);
      else await api.post(`/admin/creators/${creatorId}/videos`, form);
      toast.success("Video saved");
      setForm({ title: "", youtube_url: "" });
      setEditingId(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (vid: string) => {
    if (!window.confirm("Delete this video?")) return;
    try {
      await api.delete(`/admin/videos/${vid}`);
      toast.success("Video deleted");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-videos">
      <label className="mb-1 block text-xs font-semibold text-stone-600">Creator</label>
      <select
        data-testid="video-creator-select"
        className={`${inputCls} mb-4 max-w-xs`}
        value={creatorId}
        onChange={(e) => { setCreatorId(e.target.value); setEditingId(null); setForm({ title: "", youtube_url: "" }); }}
      >
        {creators.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>

      {creator && (
        <>
          <div className="mb-5 border border-stone-300 bg-stone-50 p-4">
            <p className="mb-3 text-xs font-bold text-stone-700">{editingId ? "Edit video" : "Add video"} — {creator.name}</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-semibold text-stone-600">Title</label>
                <input data-testid="video-form-title" className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="My Best Edit Yet" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-stone-600">YouTube URL</label>
                <input data-testid="video-form-url" className={inputCls} value={form.youtube_url} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })} placeholder="https://www.youtube.com/watch?v=…" />
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <button data-testid="video-form-save" onClick={save} className="flex items-center gap-1 bg-stone-900 px-4 py-2 text-xs font-semibold text-white hover:bg-stone-700">
                <Plus size={13} /> {editingId ? "Save changes" : "Add video"}
              </button>
              {editingId && (
                <button data-testid="video-form-cancel" onClick={() => { setEditingId(null); setForm({ title: "", youtube_url: "" }); }} className={btnCls}>Cancel</button>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            {videos.map((v) => (
              <div key={v.id} data-testid={`video-row-${v.id}`} className="flex flex-wrap items-center gap-2 border border-stone-200 px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-stone-900">{v.title}</p>
                  <p className="truncate text-xs text-stone-500">{v.youtube_url}</p>
                </div>
                <button data-testid={`video-edit-${v.id}`} onClick={() => { setEditingId(v.id); setForm({ title: v.title, youtube_url: v.youtube_url }); }} className={btnCls}><Pencil size={13} /></button>
                <button data-testid={`video-delete-${v.id}`} onClick={() => del(v.id)} className="border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50"><Trash2 size={13} /></button>
              </div>
            ))}
            {videos.length === 0 && <p className="text-xs text-stone-500">No videos yet for this creator.</p>}
          </div>
        </>
      )}
    </div>
  );
}
